import { memo, useCallback, useMemo, useState } from "react";
import { Button, Input, List, Result, Skeleton, Space } from "antd";
import InfiniteScroll from "react-infinite-scroller";
import { cleanBlankValue } from "utils/helper";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router";
import queryString from "query-string";
import { useInfiniteConversationsHistory } from "queries/conversation";
import ConversationItem from "../ConversationItem";
import { useDispatch, useSelector } from "react-redux";
import { setChat } from "appredux/chatSlice";
import {
  agentsSelector,
  conversationSelector,
  isAssignModeSelector,
  tagsSelector,
} from "appredux/selectors";
import { useTranslation } from "react-i18next";
import useDebounceValue from "hooks/useDebounceValue";
import PopOverFilter from "./PopOverFilter";
import { messageTypes } from "utils/static";
import WaveSpinnerLoader from "components/UI/customLoading";
import { includes, isEmpty, omit } from "lodash-es";

const FilterChatComp = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tags = useSelector(tagsSelector);
  const isAssignMode = useSelector(isAssignModeSelector);
  const agents = useSelector(agentsSelector);
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(() => {
    return searchParams.get("keyword") || "";
  });

  const currentConversation = useSelector(conversationSelector);

  const debounceSearch = useDebounceValue(keyword, 1000);

  const formattedParams = useMemo(() => {
    const params = queryString.parse(searchParams.toString());
    return cleanBlankValue({
      ...params,
      keyword: debounceSearch,
    });
  }, [debounceSearch, searchParams]);

  const isFiltered = useMemo(() => {
    const countParams = Object.keys(formattedParams).length;

    if (countParams > 1) {
      return true;
    }
    if (formattedParams.phone_number_id) {
      if (formattedParams.phone_number_id !== id) {
        return true;
      }
    }
    return false;
  }, [formattedParams, id]);

  const searchChange = useCallback((e) => {
    const value = e.target.value;
    setKeyword(value);
  }, []);

  const { data, isLoading, fetchNextPage, hasNextPage } =
    useInfiniteConversationsHistory(formattedParams, {
      enabled: isFiltered,
    });

  const formattedData = useMemo(() => {
    let total = 0;
    const newData =
      data?.pages.flatMap((page) => {
        total = page.total;
        return page.data;
      }) || [];

    const filteredData = newData.map((conversation) => {
      const filteredMessages = conversation.messages.filter(
        (message) => !messageTypes.SYSTEM.includes(message.type_id)
      );
      return { ...conversation, messages: filteredMessages };
    });
    return { data: filteredData, total, flatData: newData };
  }, [data?.pages]);

  const backHandler = useCallback(() => {
    navigate(`/conversations/${id}/list`);
  }, [id, navigate]);

  const sumbitFilterHandler = useCallback(
    (values) => {
      const params = {
        agent_id: values.agent_id,
        contact_id: values.contact_id,
        phone_number_id: values.phone_number_id,
        date_from: values.date_from,
        date_to: values.date_to,
        tag_ids: values.tag_ids,
        keyword: searchParams.get("keyword"),
        conv_status_id: values.conv_status_id,
      };
      setSearchParams(queryString.stringify(cleanBlankValue(params)));
    },
    [searchParams, setSearchParams]
  );

  const selectedHandler = useCallback(
    (item) => {
      const conversationCache = formattedData.flatData;
      if (Array.isArray(conversationCache)) {
        const findConversation = conversationCache.find(
          (row) => row.id === item.id
        );
        if (!isEmpty(findConversation)) {
          dispatch(
            setChat({
              conversation: omit(item, "messages"),
              messages: findConversation.messages,
            })
          );
        }
      }
    },
    [dispatch, formattedData]
  );

  const renderItem = useCallback(
    (item, index) => {
      let filterTags = [];
      if (!isEmpty(item?.contact?.tag_ids)) {
        filterTags = tags.filter((tag) =>
          includes(item?.contact.tag_ids, tag.id)
        );
      }
      let agentName = "";
      if (isAssignMode) {
        agentName = agents.find(
          (agent) => agent.id === item.current_agent_id
        )?.name;
      }
      return (
        <ConversationItem
          onSelect={() => {
            selectedHandler(item);
          }}
          key={item.id}
          item={item}
          index={index}
          isSelected={currentConversation?.id === item.id}
          tags={filterTags}
          assignedAgent={agentName}
          search={debounceSearch}
          isSearch
        />
      );
    },
    [
      isAssignMode,
      currentConversation?.id,
      debounceSearch,
      tags,
      agents,
      selectedHandler,
    ]
  );

  const searchBlur = useCallback(() => {
    const params = queryString.parse(searchParams.toString());
    setSearchParams(cleanBlankValue({ ...params, keyword }));
  }, [keyword, searchParams, setSearchParams]);

  return (
    <>
      <div className="pb-2 pt-4 px-3 bg-gray-50 flex flex-row gap-x-2">
        <Space.Compact block>
          <Button
            type="primary"
            icon={<ArrowLeftOutlined className="text-white" />}
            onClick={backHandler}
          />
          <Input
            className="mb-2"
            placeholder="Search..."
            autoFocus
            defaultValue={keyword}
            // value={keyword}
            onChange={searchChange}
            allowClear
            onBlur={searchBlur}
          />
        </Space.Compact>

        <PopOverFilter data={formattedParams} onSubmit={sumbitFilterHandler} />
      </div>
      <div
        id="conversationsSearch"
        className="bg-grey-lighter overflow-y-auto flex-1 pb-4"
      >
        <InfiniteScroll
          // pageStart={1}
          loadMore={fetchNextPage}
          hasMore={hasNextPage}
          loader={
            <Skeleton
              avatar
              paragraph={{
                rows: 1,
              }}
              active
              className="px-3 mt-3"
              key={0}
            />
          }
          useWindow={false}
          initialLoad={false}
        >
          {isLoading ? (
            <WaveSpinnerLoader classNameProps="mt-10" />
          ) : isEmpty(formattedData.data) ? (
            <div className="flex w-full h-full justify-center ">
              <Result
                status="404"
                title={t("no_data")}
                subTitle={
                  !isFiltered ? t("please_fill_one_of_filter_first") : undefined
                }
              />
            </div>
          ) : (
            <List dataSource={formattedData.data} renderItem={renderItem} />
          )}
        </InfiniteScroll>
      </div>
    </>
  );
};

const FilterChat = memo(FilterChatComp);

export default FilterChat;
